import { useAuth } from '@/auth/useAuth';
import { Badge, Button, Card, CardBody, Input, LoadingContainer, Modal, Select } from '@/components/ui';
import { useAsesoriaActiva, useEmpresa, useNtcById, useRequisitosEstadoActual } from '@/hooks';
import { EstadoActualRequisito, RequisitoConAsesoria, RequisitoEvidencias } from '@/services/requisitos.service';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

function getEvaluacionClass(evaluacion?: string): string {
  switch (evaluacion) {
    case 'C':
      return 'bg-green-100 text-green-800';
    case 'NC':
      return 'bg-red-100 text-red-800';
    case 'CP':
      return 'bg-yellow-100 text-yellow-800';
    case 'NA':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

// Detectar si un valor es un NIT válido (solo dígitos, tamaño 7 a 12)
function isNit(str: string): boolean {
  return /^\d{7,12}$/.test(str);
}

export function NTCDetallePage() {
  const { nit, ntcId } = useParams<{ nit: string; ntcId: string }>();
  const navigate = useNavigate();

  // Guardar el estado de si hay parámetros de ruta para evitar hooks condicionales
  const hasParams = Boolean(nit && ntcId && isNit(nit));

  // Hooks called unconditionally
  const { user } = useAuth();
  const asesorId = user?.asesor?.id;
  const { empresa, isLoading: empresaLoading } = useEmpresa(nit || '');

  // Obtener asesoría activa usando el hook, habilitado solo si la empresa tiene una activa
  const { data: asesoriaActiva, isLoading: asesoriaLoading } = useAsesoriaActiva(nit || '', !!empresa?.asesoriaActivaId);

  const asesoriaId = asesoriaActiva?.asesoriaId;

  // Usar ntcId como string en todos los hooks y filtros
  const { data: ntc, isLoading: ntcsLoading } = useNtcById(nit || '', ntcId ? String(ntcId) : '', hasParams);
  // Obtener requisitos si hay asesoría activa
  const { requisitos, isLoading: requisitosLoading } = useRequisitosEstadoActual(
    nit || '',
    ntc?.id ? String(ntc.id) : undefined, // Usar ntcId real del objeto ntc, no del parámetro
    hasParams && !!empresa && !!asesorId && !!ntc
  );

  const [selectedRequisito, setSelectedRequisito] = useState<EstadoActualRequisito | null>(null);
  const [filtroEvaluacion, setFiltroEvaluacion] = useState('');
  const [busqueda, setBusqueda] = useState('');

  // Conditional returns after all hooks
  if (!hasParams) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-700">Parámetros inválidos</h2>
        <Link to={`/empresa/${nit}`}>
          <Button variant="primary" className="mt-4">
            Volver a empresa
          </Button>
        </Link>
      </div>
    );
  }

  if (asesoriaLoading) {
    return <LoadingContainer text="Cargando asesoría activa..." />;
  }

  if (empresaLoading || !empresa || !asesorId) {
    return <LoadingContainer text="Cargando NTC..." />;
  }

  // Requisitos ya vienen filtrados del backend por ntcId
  // Solo aplicar filtros de evaluación y búsqueda local
  const requisitosFiltrados = requisitos.filter((req) => {
    const evaluacion = req.calificacion || 'NC';
    if (filtroEvaluacion && evaluacion !== filtroEvaluacion) return false;
    return (
      !busqueda || req.capitulo.toLowerCase().includes(busqueda.toLowerCase()) || req.numeral.toLowerCase().includes(busqueda.toLowerCase())
    );
  });

  const isLoading = requisitosLoading || ntcsLoading;

  if (isLoading) {
    return <LoadingContainer text="Cargando NTC..." />;
  }

  // Validar que el NTC exista y tenga datos válidos
  if (!ntc || !ntc.id || ntc.totalRequisitos === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-700">NTC no encontrado</h2>
        <p className="text-gray-500 mt-2">El NTC {ntcId} no existe o no está asignado a esta empresa.</p>
        <div className="mt-4 flex flex-col gap-2">
          <Link to={`/empresa/${nit}`}>
            <Button variant="primary">Volver a empresa</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Botón volver */}
      <Button variant="secondary" size="sm" onClick={() => navigate(`/empresa/${nit}`)} className="mb-4">
        ← Volver a empresa
      </Button>

      {/* Header */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{ntc.nombre}</h1>
              <p className="text-gray-500">
                NTC {ntc.id} - {empresa.nombre} - NIT: {empresa.nit}
              </p>
            </div>
            <div className="text-right">
              <div
                className={`text-2xl font-bold ${ntc.porcentajeCumplimiento >= 80 ? 'text-green-600' : ntc.porcentajeCumplimiento >= 50 ? 'text-yellow-600' : 'text-red-600'}`}
              >
                {ntc.porcentajeCumplimiento || 0}%
              </div>
              <p className="text-sm text-gray-500">Cumplimiento</p>
            </div>
          </div>

          {/* Estadísticas */}
          <div className="grid grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{ntc.totalC || 0}</div>
              <div className="text-xs text-gray-500">Cumple</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-red-600">{ntc.totalNC || 0}</div>
              <div className="text-xs text-gray-500">No Cumple</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-yellow-600">{ntc.totalCP || 0}</div>
              <div className="text-xs text-gray-500">Parcial</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-600">{ntc.totalNA || 0}</div>
              <div className="text-xs text-gray-500">No Aplica</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{ntc.totalRequisitos || 0}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Filtros */}
      <div className="flex gap-4 mb-6">
        <Select
          value={filtroEvaluacion}
          onChange={(e) => setFiltroEvaluacion(e.target.value)}
          options={[
            { value: '', label: 'Todos' },
            { value: 'C', label: 'Cumple (C)' },
            { value: 'NC', label: 'No Cumple (NC)' },
            { value: 'CP', label: 'Parcial (CP)' },
            { value: 'NA', label: 'No Aplica (NA)' },
          ]}
          className="w-48"
        />
        <Input
          type="text"
          placeholder="Buscar requisito..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="flex-1"
        />
      </div>

      {/* Lista de requisitos */}
      <div className="space-y-3">
        {requisitosFiltrados.map((req) => (
          <Card key={req.requisitoId} className="hover:shadow-md transition-shadow">
            <CardBody className="py-3">
              <div className="flex items-center gap-4">
                {/* Numeral */}
                <div className="flex flex-col flex-1">
                  {req.capitulo}

                  {/* Requisito */}
                  <div>{req.numeral}</div>
                </div>

                {/* Evaluación */}
                <div className="shrink-0">
                  <Badge className={getEvaluacionClass(req.calificacion || 'NC')}>{req.calificacion || 'NC'}</Badge>
                </div>

                {/* Acciones */}
                <div className="shrink-0 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedRequisito(req)}>
                    Ver
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {requisitosFiltrados.length === 0 && (
        <div className="text-center py-12 text-gray-500">No se encontraron requisitos con los filtros aplicados</div>
      )}

      {/* Modal de detalle de requisito */}
      <Modal isOpen={!!selectedRequisito} onClose={() => setSelectedRequisito(null)} title="Detalle del Requisito" size="lg">
        {selectedRequisito && (
          <div className="space-y-4">
            {/* Header con NTC y evaluación */}
            <div className="flex items-center justify-between">
              <span className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">{selectedRequisito.ntc.nombre}</span>
              <Badge className={getEvaluacionClass(selectedRequisito.calificacion || 'NC')}>{selectedRequisito.calificacion || 'NC'}</Badge>
            </div>

            {/* Capítulo y Numeral verticalmente */}
            <div className="space-y-2">
              <div>
                <span className="block text-sm font-medium text-gray-500">Capítulo</span>
                <p className="text-gray-800 font-medium">{selectedRequisito.capitulo}</p>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-500">Numeral</span>
                <p className="text-gray-800">{selectedRequisito.numeral}</p>
              </div>
            </div>

            {/* Requisito */}
            <div>
              <span className="block text-sm font-medium text-gray-500 mb-1">Requisito</span>
              <p className="text-gray-800 whitespace-pre-line">{selectedRequisito.descripcion}</p>
            </div>

            {/* Evidencias Sugeridas como lista */}
            {selectedRequisito.posiblesEvidencias && selectedRequisito.posiblesEvidencias.length > 0 && (
              <div>
                <span className="block text-sm font-medium text-gray-500 mb-2">Evidencias Sugeridas</span>
                <ul className="space-y-2 flex gap-2 flex-wrap">
                  {selectedRequisito.posiblesEvidencias.map((ev) => (
                    <li key={ev.evidenciaId} className="flex items-center gap-2 list-decimal px-2.5 py-1 bg-gray-100 h-full rounded-full">
                      {ev.url ? (
                        <a href={ev.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline list-none">
                          {ev.nombre}
                        </a>
                      ) : (
                        <span className="text-gray-600">{ev.nombre}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedRequisito.asesoria?.observaciones && (
              <div>
                <span className="block text-sm font-medium text-gray-500 mb-1">Observaciones</span>
                <p className="text-gray-600">{selectedRequisito.asesoria?.observaciones}</p>
              </div>
            )}

            {selectedRequisito.asesoria?.planMejora && (
              <div>
                <span className="block text-sm font-medium text-gray-500 mb-1">Plan de Mejora</span>
                <p className="text-gray-600">{selectedRequisito.asesoria?.planMejora}</p>
              </div>
            )}

            {/* Acciones */}
            {asesoriaId && (
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <Link to={`/empresa/${nit}/asesoria/evaluacion?requisitoId=${selectedRequisito.requisitoId}`}>
                  <Button>Ir a Evaluar</Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
